import React, { useState, useEffect } from 'react';
import { IconButton, Typography, Tabs, Tab, Box, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import SearchableDropdown from 'allGlobalComponents/SearchableDropdown';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import RichTextEditor from './RichTextEditor';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
		padding: theme.spacing(2)
	},
	fieldRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5)
	},
	fieldLabel: {
		fontSize: '1.2rem',
		color: theme.palette.text.secondary,
		fontWeight: 500
	},
	requiredStar: {
		color: theme.palette.error.main,
		marginLeft: 2
	},
	attachmentsContainer: {
		display: 'flex',
		gap: theme.spacing(2),
		flexWrap: 'wrap',
		marginTop: theme.spacing(2)
	},
	attachmentItem: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1, 1.5),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.background.paper,
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	attachmentIcon: {
		color: theme.palette.text.secondary
	},
	attachmentInfo: {
		display: 'flex',
		flexDirection: 'column'
	},
	attachmentName: {
		fontSize: '0.875rem',
		color: theme.palette.text.primary
	},
	attachmentSize: {
		fontSize: '0.75rem',
		color: theme.palette.text.secondary
	},
	attachmentRemove: {
		marginLeft: theme.spacing(1),
		padding: 2
	},
	bottomActions: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%'
	},
	leftActions: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	rightActions: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	emailPillsContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		padding: theme.spacing(1),
		border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'}`,
		borderRadius: 4,
		minHeight: 40,
		cursor: 'text',
		backgroundColor: theme.palette.type === 'dark' ? 'transparent' : '#FAFAFA',
		'&:hover': {
			borderColor: theme.palette.text.primary
		},
		'&:focus-within': {
			borderColor: theme.palette.primary.main,
			borderWidth: 2
		}
	},
	emailPill: {
		display: 'flex',
		alignItems: 'center',
		backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#E5E7EB',
		borderRadius: 16,
		padding: '2px 8px 2px 12px',
		fontSize: 13,
		color: theme.palette.text.primary
	},
	emailPillText: {
		marginRight: 4
	},
	emailPillRemove: {
		padding: 2,
		marginLeft: 2,
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : '#D1D5DB',
			borderRadius: '50%'
		}
	},
	emailInput: {
		flex: 1,
		minWidth: 150,
		border: 'none',
		outline: 'none',
		backgroundColor: 'transparent',
		fontSize: 14,
		color: theme.palette.text.primary,
		'&::placeholder': {
			color: theme.palette.text.secondary
		}
	},
	errorText: {
		color: theme.palette.error.main,
		fontSize: 12,
		marginTop: theme.spacing(0.5)
	},
	tabsContainer: {
		backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#F3F4F6',
		borderRadius: 8,
		marginTop: theme.spacing(1)
	},
	tab: {
		minHeight: 44,
		textTransform: 'none',
		fontSize: 14,
		fontWeight: 500,
		color: theme.palette.text.secondary,
		borderBottom: '2px solid transparent',
		'&.Mui-selected': {
			color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.08)',
			borderBottom: `2px solid ${theme.palette.type === 'dark' ? '#60A5FA' : theme.palette.primary.main}`
		},
		'& .MuiTab-wrapper': {
			flexDirection: 'row',
			gap: theme.spacing(0.5)
		}
	},
	tabIcon: {
		fontSize: 20,
		marginRight: theme.spacing(0.5)
	},
	previewContainer: {
		padding: theme.spacing(2),
		minHeight: 200,
		maxHeight: 400,
		overflow: 'auto',
		backgroundColor: theme.palette.background.paper,
		border: 'none',
		borderRadius: 0
	},
	previewContent: {
		color: theme.palette.text.primary,
		fontSize: 15,
		lineHeight: 1.7,
		'& img': { maxWidth: '100%', height: 'auto' },
		'& a': { color: theme.palette.primary.main },
		'& ul, & ol': { paddingLeft: theme.spacing(3), marginLeft: theme.spacing(2) },
		'& li': { marginBottom: theme.spacing(0.5) },
		'& blockquote': { borderLeft: `3px solid ${theme.palette.divider}`, paddingLeft: theme.spacing(2), marginLeft: 0, color: theme.palette.text.secondary },
		'& .param-highlight': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.25)',
			color: theme.palette.type === 'dark' ? '#86EFAC' : '#166534',
			padding: '2px 6px', borderRadius: 4, fontWeight: 600,
			border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.4)'}`,
			display: 'inline-block'
		},
		'& .param-unfilled': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.25)',
			color: theme.palette.type === 'dark' ? '#FCD34D' : '#B45309',
			padding: '2px 6px', borderRadius: 4, fontWeight: 500,
			border: `1px dashed ${theme.palette.type === 'dark' ? 'rgba(251, 191, 36, 0.5)' : 'rgba(251, 191, 36, 0.5)'}`,
			display: 'inline-block', fontStyle: 'italic'
		}
	},
	emptyPreview: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 200,
		color: theme.palette.text.secondary,
		fontStyle: 'italic',
		fontSize: 14,
		backgroundColor: theme.palette.background.paper
	},
	characterCount: {
		padding: theme.spacing(0.5, 1),
		fontSize: '1.2rem',
		color: theme.palette.type === 'dark' ? '#9CA3AF' : theme.palette.text.secondary,
		textAlign: 'right'
	},
	characterCountWarning: { color: theme.palette.warning.main },
	characterCountError: { color: theme.palette.error.main },
	paramsSection: {
		marginTop: theme.spacing(2), padding: theme.spacing(2), borderRadius: 8,
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
		border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`
	},
	paramsTitle: {
		fontSize: 14, fontWeight: 600,
		color: theme.palette.type === 'dark' ? '#93C5FD' : '#2563EB',
		marginBottom: theme.spacing(1.5), display: 'flex', alignItems: 'center', gap: theme.spacing(1)
	},
	paramField: { marginBottom: theme.spacing(1.5) },
	paramLabel: { fontSize: 13, fontWeight: 500, color: theme.palette.text.secondary, marginBottom: theme.spacing(0.5) },
	editorSection: {
		border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'}`,
		borderRadius: 4, overflow: 'hidden', marginTop: theme.spacing(1)
	},
	htmlCodeContainer: {
		paddingTop: theme.spacing(7),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		minHeight: 200,
		maxHeight: 400,
		overflow: 'auto',
		backgroundColor: theme.palette.background.paper,
		position: 'relative'
	},
	htmlCodeHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		zIndex: 10,
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(1, 2),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	copyButton: {
		display: 'flex', alignItems: 'center', gap: theme.spacing(0.5),
		padding: theme.spacing(0.5, 1.5), fontSize: 13, fontWeight: 500,
		color: theme.palette.type === 'dark' ? '#E5E7EB' : '#374151',
		backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#E5E7EB',
		border: 'none', borderRadius: 6, cursor: 'pointer',
		'&:hover': { backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : '#D1D5DB' }
	},
	copyButtonSuccess: {
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
		color: '#22C55E'
	},
	pasteButton: {
		display: 'flex', alignItems: 'center', gap: theme.spacing(0.5),
		padding: theme.spacing(0.5, 1.5), fontSize: 13, fontWeight: 500,
		color: theme.palette.type === 'dark' ? '#93C5FD' : '#2563EB',
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
		border: 'none', borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s ease',
		'&:hover': { backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)' }
	},
	clearButton: {
		display: 'flex', alignItems: 'center', gap: theme.spacing(0.5),
		padding: theme.spacing(0.5, 1.5), fontSize: 13, fontWeight: 500,
		color: theme.palette.type === 'dark' ? '#F87171' : '#DC2626',
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
		border: 'none', borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s ease',
		'&:hover': { backgroundColor: theme.palette.type === 'dark' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.15)' }
	},
	htmlCode: {
		fontFamily: 'monospace', fontSize: 13,
		color: theme.palette.type === 'dark' ? '#E6EDF3' : '#24292F',
		whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0
	},
	htmlButtonsRow: { display: 'flex', gap: theme.spacing(1) },
	modeToggle: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) },
	modeButton: {
		padding: theme.spacing(0.75, 2), fontSize: 14, fontWeight: 500, borderRadius: 6,
		border: `1px solid ${theme.palette.divider}`, backgroundColor: 'transparent',
		color: theme.palette.text.secondary, cursor: 'pointer', transition: 'all 0.2s ease',
		'&:hover': { backgroundColor: theme.palette.action.hover }
	},
	modeButtonActive: {
		backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#1F1E30',
		color: '#FFFFFF !important',
		borderColor: theme.palette.type === 'dark' ? '#374151' : '#1F1E30',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? '#4B5563' : '#2D2C45 !important',
			color: '#FFFFFF !important'
		}
	},
	previewLegend: {
		display: 'flex', alignItems: 'center', gap: theme.spacing(2), marginBottom: theme.spacing(1.5),
		padding: theme.spacing(1),
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
		borderRadius: 6, fontSize: 13
	},
	legendItem: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
	legendFilledBadge: {
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.25)',
		color: theme.palette.type === 'dark' ? '#86EFAC' : '#166534',
		padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 12,
		border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.4)'}`
	},
	legendUnfilledBadge: {
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.25)',
		color: theme.palette.type === 'dark' ? '#FCD34D' : '#B45309',
		padding: '2px 8px', borderRadius: 4, fontWeight: 500, fontSize: 12,
		border: `1px dashed ${theme.palette.type === 'dark' ? 'rgba(251, 191, 36, 0.5)' : 'rgba(251, 191, 36, 0.5)'}`,
		fontStyle: 'italic'
	},
	legendText: { color: theme.palette.text.secondary },
	previewLabel: { fontSize: 13, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: theme.spacing(1), marginTop: theme.spacing(2) },
	loadingText: {
		fontSize: 12,
		color: theme.palette.text.secondary,
		fontStyle: 'italic'
	},
	twoColumnRow: {
		display: 'flex',
		gap: theme.spacing(2),
		'& > div': {
			flex: 1
		}
	},
	// Save as Template Dialog styles
	saveTemplateDialog: {
		'& .MuiDialog-paper': {
			borderRadius: 12,
			minWidth: 400
		}
	},
	saveTemplateDialogTitle: {
		backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#F3F4F6',
		borderBottom: `1px solid ${theme.palette.divider}`,
		padding: theme.spacing(2, 3),
		'& .MuiTypography-root': {
			fontSize: 18,
			fontWeight: 600
		}
	},
	saveTemplateDialogContent: {
		padding: theme.spacing(3)
	},
	saveTemplateDialogActions: {
		padding: theme.spacing(2, 3),
		borderTop: `1px solid ${theme.palette.divider}`,
		gap: theme.spacing(1)
	},
	templateNameError: {
		color: theme.palette.error.main,
		fontSize: '1rem',
		fontWeight: 600,
		marginTop: theme.spacing(1),
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	inputError: {
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				borderColor: theme.palette.error.main
			},
			'&:hover fieldset': {
				borderColor: theme.palette.error.main
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.error.main
			}
		}
	}
}));

function ComposePushEmailModal({ open, onClose, onSend, userData = null }) {
	const classes = useStyles();
	const [mode, setMode] = useState('compose');
	const [senderDomains, setSenderDomains] = useState([]);
	const [loadingDomains, setLoadingDomains] = useState(false);
	const [selectedDomain, setSelectedDomain] = useState(null);
	const [senderEmails, setSenderEmails] = useState([]);
	const [loadingSenderEmails, setLoadingSenderEmails] = useState(false);
	const [selectedSenderEmail, setSelectedSenderEmail] = useState(null);
	const [templates, setTemplates] = useState([]);
	const [loadingTemplates, setLoadingTemplates] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [detectedParams, setDetectedParams] = useState([]);
	const [paramValues, setParamValues] = useState({});
	const [activeTab, setActiveTab] = useState(0);
	const [editorBody, setEditorBody] = useState('');
	const [htmlCopied, setHtmlCopied] = useState(false);
	const [formData, setFormData] = useState({ subject: '' });
	const [toEmails, setToEmails] = useState([]);
	const [toInput, setToInput] = useState('');
	const [toError, setToError] = useState('');
	const [attachments, setAttachments] = useState([]);
	const [uploadingAttachment, setUploadingAttachment] = useState(false);
	const [sendingEmail, setSendingEmail] = useState(false);
	const [subjectLength, setSubjectLength] = useState(0);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

	// Save as Template states
	const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
	const [templateName, setTemplateName] = useState('');
	const [templateNameLength, setTemplateNameLength] = useState(0);
	const [templateNameError, setTemplateNameError] = useState('');
	const [savingTemplate, setSavingTemplate] = useState(false);
	const [templateSaved, setTemplateSaved] = useState(false);

	const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
	const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

	const fetchSenderDomains = () => {
		const apiPayload = {};
		if (userData?.id) apiPayload.clientId = userData?.id;
		setLoadingDomains(true);
		CoreHttpHandler.request('channels', 'emailSenderDomainList', apiPayload,
			response => {
				const domains = response.data.data.data.domainsList || [];
				setSenderDomains(domains);
				setLoadingDomains(false);
			},
			error => { console.error('Error fetching sender domains:', error); setSenderDomains([]); setLoadingDomains(false); }
		);
	};

	const fetchSenderEmailsByDomain = (domainId) => {
		setLoadingSenderEmails(true);
		setSenderEmails([]);
		setSelectedSenderEmail(null);

		const apiPayload = {
			page: 0,
			limit: 999999,
			senderDomainId: domainId.toString()
		};
		if (userData?.id) apiPayload.clientId = userData?.id;

		CoreHttpHandler.request('emailCampaign', 'senderEmailFetchByDomain', apiPayload,
			response => {
				const emails = response.data?.data?.data?.emailsList || [];
				setSenderEmails(emails);
				setLoadingSenderEmails(false);
			},
			error => {
				console.error('Error fetching sender emails:', error);
				setSenderEmails([]);
				setLoadingSenderEmails(false);
			}
		);
	};

	const fetchTemplates = () => {
		setLoadingTemplates(true);
		const apiPayload = { page: 0, limit: 999999 };
		if (userData?.id) apiPayload.clientId = userData?.id;
		CoreHttpHandler.request('emailCampaign', 'emailTemplateList', apiPayload,
			response => { setTemplates(response.data?.data?.data?.emailTemplates || []); setLoadingTemplates(false); },
			error => { console.error('Error fetching templates:', error); setTemplates([]); setLoadingTemplates(false); }
		);
	};

	useEffect(() => {
		if (open) {
			fetchSenderDomains();
			fetchTemplates();
			resetForm();
		}
	}, [open]);

	const resetForm = () => {
		setMode('compose');
		setSelectedDomain(null);
		setSelectedSenderEmail(null);
		setSenderEmails([]);
		setToEmails([]);
		setToInput('');
		setToError('');
		setFormData({ subject: '' });
		setSubjectLength(0);
		setAttachments([]);
		setActiveTab(0);
		setSelectedTemplate(null);
		setDetectedParams([]);
		setParamValues({});
		setEditorBody('');
		setHtmlCopied(false);
		setSnackbar({ open: false, message: '', severity: 'success' });
		// Reset save template states
		setSaveTemplateDialogOpen(false);
		setTemplateName('');
		setTemplateNameLength(0);
		setTemplateNameError('');
		setSavingTemplate(false);
		setTemplateSaved(false);
	};

	const handleDomainChange = (newValue) => {
		setSelectedDomain(newValue);
		setSelectedSenderEmail(null);
		setSenderEmails([]);

		if (newValue) {
			fetchSenderEmailsByDomain(newValue.value);
		}
	};

	const handleSenderEmailChange = (newValue) => {
		setSelectedSenderEmail(newValue);
	};

	const formattedDomainOptions = senderDomains.map(d => ({
		value: d.id,
		label: `${d.name} (${d.domain})`
	}));

	const formattedSenderEmailOptions = senderEmails.map(e => ({
		value: e.id,
		label: e.email
	}));

	const detectParams = (html) => {
		const paramMatches = html?.match(/\{\{\d+\}\}/g) || [];
		return [...new Set(paramMatches)].sort((a, b) => parseInt(a.match(/\d+/)[0]) - parseInt(b.match(/\d+/)[0]));
	};

	const handleTemplateChange = (newValue) => {
		setSelectedTemplate(newValue);
		if (newValue) {
			const template = templates.find(t => t.id === newValue.value);
			if (template) {
				const params = detectParams(template.email_template_html || template.email_template_body);
				setDetectedParams(params);
				const initialValues = {};
				params.forEach(param => { initialValues[param] = ''; });
				setParamValues(initialValues);
			}
		} else { setDetectedParams([]); setParamValues({}); }
	};

	const handleParamChange = (param) => (e) => setParamValues(prev => ({ ...prev, [param]: e.target.value }));
	const areAllParamsFilled = () => detectedParams.length === 0 || detectedParams.every(param => paramValues[param]?.trim());

	const replaceParamsInHtml = (html) => {
		let result = html;
		Object.keys(paramValues).forEach(param => {
			result = result.replace(new RegExp(param.replace(/[{}]/g, '\\$&'), 'g'), paramValues[param] || param);
		});
		return result;
	};

	const replaceParamsInHtmlWithHighlight = (html) => {
		let result = html;
		Object.keys(paramValues).forEach(param => {
			const regex = new RegExp(param.replace(/[{}]/g, '\\$&'), 'g');
			const value = paramValues[param];
			result = result.replace(regex, value?.trim()
				? `<span class="param-highlight">${value}</span>`
				: `<span class="param-unfilled">${param}</span>`);
		});
		return result;
	};

	const getPreviewHtml = () => {
		if (mode === 'compose') return editorBody;
		if (!selectedTemplate) return '';
		const template = templates.find(t => t.id === selectedTemplate.value);
		if (!template) return '';
		let html = template.email_template_html || '';
		const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
		if (bodyMatch) html = bodyMatch[1];
		return replaceParamsInHtmlWithHighlight(html);
	};

	const generateCompleteHTML = (bodyContent) => `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Email</title>
<style>body{font-family:Arial,sans-serif;line-height:1.6;color:#333333;margin:0;padding:20px}img{max-width:100%;height:auto}a{color:#1a73e8}</style>
</head><body>${bodyContent || ''}</body></html>`;

	const formatHtml = (html) => html ? html.replace(/></g, '>\n<').replace(/(<[^/][^>]*>)/g, '\n$1').replace(/(<\/[^>]+>)/g, '$1\n').replace(/\n\s*\n/g, '\n').trim() : '';

	const copyHtmlToClipboard = () => {
		navigator.clipboard.writeText(formatHtml(generateCompleteHTML(editorBody))).then(() => {
			setHtmlCopied(true); setTimeout(() => setHtmlCopied(false), 2000);
		}).catch(err => console.error('Failed to copy:', err));
	};

	const pasteHtmlFromClipboard = async () => {
		try {
			const clipboardText = await navigator.clipboard.readText();
			if (clipboardText) {
				let bodyContent = clipboardText;
				const bodyMatch = clipboardText.match(/<body[^>]*>([\s\S]*)<\/body>/i);
				if (bodyMatch) bodyContent = bodyMatch[1];
				setEditorBody(bodyContent);
			}
		} catch (err) { console.error('Failed to paste:', err); showSnackbar('Failed to paste from clipboard', 'error'); }
	};

	const clearHtmlContent = () => setEditorBody('');
	const handleEditorBodyChange = (html) => setEditorBody(html);

	const uploadFileToServer = (file) => new Promise((resolve, reject) => {
		const form = new FormData();
		form.append('file', file, `${Date.now()}_${file.name.replace(/\s/g, '')}`);
		CoreHttpHandler.request('content', 'upload', { params: form }, res => resolve(res.data.data.link), error => reject(error));
	});

	const handleImageUpload = async (file) => {
		try { return await uploadFileToServer(file); }
		catch (error) { console.error('Failed to upload image:', error); return URL.createObjectURL(file); }
	};

	const formattedTemplateOptions = templates.map(t => ({ value: t.id, label: t.email_template_name }));
	const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

	const addEmailToPills = (email) => {
		const trimmedEmail = email.trim();
		if (!trimmedEmail) return false;
		if (!isValidEmail(trimmedEmail)) { setToError('Invalid email address'); return false; }
		if (toEmails.includes(trimmedEmail)) { setToError('Email already added'); return false; }
		setToEmails([...toEmails, trimmedEmail]); setToInput(''); setToError(''); return true;
	};

	const handleToInputChange = (e) => {
		const value = e.target.value;
		const inputType = e.nativeEvent.inputType;

		const isPaste = inputType === 'insertFromPaste';

		if (isPaste) {
			if (value.includes(',') || value.includes(';') || value.includes('\n')) {
				const emails = value.split(/[,;\n]+/).map(email => email.trim()).filter(email => email);
				emails.forEach(email => {
					if (isValidEmail(email) && !toEmails.includes(email)) {
						setToEmails(prev => [...prev, email]);
					}
				});
				setToInput('');
				setToError('');
				return;
			}

			const trimmedValue = value.trim();
			if (trimmedValue && isValidEmail(trimmedValue)) {
				addEmailToPills(trimmedValue);
				return;
			}
		}

		if (value.endsWith(' ')) {
			const email = value.trim();
			if (email) addEmailToPills(email);
		} else {
			setToInput(value);
			setToError('');
		}
	};

	const handleToKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addEmailToPills(toInput); }
		if (e.key === 'Backspace' && !toInput && toEmails.length > 0) setToEmails(toEmails.slice(0, -1));
	};

	const handleRemoveEmail = (emailToRemove) => setToEmails(toEmails.filter(email => email !== emailToRemove));

	const handleFieldChange = (field) => (e) => {
		const value = e.target.value;
		if (field === 'subject') { if (value.length <= 100) { setFormData(prev => ({ ...prev, [field]: value })); setSubjectLength(value.length); } }
		else setFormData(prev => ({ ...prev, [field]: value }));
	};

	const handleTabChange = (event, newValue) => setActiveTab(newValue);

	const handleAttachFile = async () => {
		const input = document.createElement('input');
		input.type = 'file'; input.multiple = true;
		input.onchange = async (e) => {
			const files = Array.from(e.target.files);
			const maxSize = 10 * 1024 * 1024; // 10MB in bytes

			setUploadingAttachment(true);
			for (const file of files) {
				// Check file size
				if (file.size > maxSize) {
					showSnackbar(`File "${file.name}" exceeds 10MB limit. Skipping...`, 'error');
					continue;
				}

				try {
					const uploadedUrl = await uploadFileToServer(file);
					setAttachments(prev => [...prev, { id: Date.now() + Math.random(), name: file.name, size: formatFileSize(file.size), url: uploadedUrl }]);
				} catch (error) {
					console.error('Failed to upload attachment:', error);
					showSnackbar(`Failed to upload "${file.name}"`, 'error');
				}
			}
			setUploadingAttachment(false);
		};
		input.click();
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024, sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	const handleRemoveAttachment = (id) => setAttachments(prev => prev.filter(att => att.id !== id));
	const stripHtmlTags = (html) => { const tmp = document.createElement('div'); tmp.innerHTML = html; return tmp.textContent || tmp.innerText || ''; };

	// Save as Template functions
	const handleOpenSaveTemplateDialog = () => {
		setTemplateName('');
		setTemplateNameLength(0);
		setTemplateNameError('');
		setSaveTemplateDialogOpen(true);
	};

	const handleCloseSaveTemplateDialog = () => {
		setSaveTemplateDialogOpen(false);
		setTemplateName('');
		setTemplateNameLength(0);
		setTemplateNameError('');
	};

	const handleTemplateNameChange = (e) => {
		let value = e.target.value;
		value = value.replace(/\s/g, '_');

		if (value.length <= 100) {
			setTemplateName(value);
			setTemplateNameLength(value.length);

			if (value.trim()) {
				const nameExists = templates.some(
					template => template.email_template_name?.toLowerCase() === value.trim().toLowerCase()
				);
				if (nameExists) {
					setTemplateNameError(`Template name "${value.trim()}" already exists. Please choose a different name.`);
				} else {
					setTemplateNameError('');
				}
			} else {
				setTemplateNameError('');
			}
		}
	};

	const handleSaveAsTemplate = () => {
		if (!templateName.trim()) {
			setTemplateNameError('Please enter a template name');
			return;
		}

		if (templateNameError) {
			return;
		}

		setSavingTemplate(true);
		const apiPayload = {
			email_template_name: templateName.trim(),
			email_template_body: stripHtmlTags(editorBody),
			email_template_html: generateCompleteHTML(editorBody)
		};

		if (userData?.id) {
			apiPayload.clientId = userData?.id;
		}

		CoreHttpHandler.request(
			'emailCampaign',
			'emailTemplateAdd',
			apiPayload,
			response => {
				setSavingTemplate(false);
				if (response.data?.code === 200) {
					showSnackbar('Template saved successfully!', 'success');
					setTemplateSaved(true);
					handleCloseSaveTemplateDialog();
					// Refresh templates list
					fetchTemplates();
				} else if (response.data?.code != 200) {
					setTemplateNameError(response.data?.message);
				}
			},
			error => {
				setSavingTemplate(false);
				const errorMessage = error.response?.data?.message || 'Failed to save template';
				if (errorMessage.toLowerCase().includes('exists') || errorMessage.toLowerCase().includes('duplicate')) {
					setTemplateNameError(errorMessage);
				} else {
					showSnackbar(errorMessage, 'error');
				}
			}
		);
	};

	const handleSend = async () => {
		if (!selectedDomain) { showSnackbar('Please select a sender domain', 'error'); return; }
		if (!selectedSenderEmail) { showSnackbar('Please select a sender email', 'error'); return; }
		if (toEmails.length === 0) { setToError('Please add at least one recipient'); return; }
		if (mode === 'compose' && !editorBody.trim()) { showSnackbar('Please enter email content', 'error'); return; }
		if (mode === 'template' && !selectedTemplate) { showSnackbar('Please select a template', 'error'); return; }

		setSendingEmail(true);
		try {
			let completeHTML = '', plainText = '';
			if (mode === 'compose') {
				completeHTML = generateCompleteHTML(editorBody);
				plainText = stripHtmlTags(editorBody);
			} else {
				const template = templates.find(t => t.id === selectedTemplate.value);
				completeHTML = replaceParamsInHtml(template.email_template_html);
				plainText = stripHtmlTags(replaceParamsInHtml(template.email_template_body || template.email_template_html));
			}

			const payload = {
				sender_domain_id: selectedDomain.value.toString(),
				sender_email_id: selectedSenderEmail.value,
				receiver_email: toEmails,
				subject: formData.subject || '',
				text: plainText,
				html: completeHTML,
				campaign_id: 0
			};

			// Format attachments as API expects: [{ url: "...", filename: "..." }]
			if (attachments.length > 0) {
				payload.attachments = attachments.map(att => ({
					url: att.url,
					filename: att.name
				}));
			}

			if (userData?.id) payload.clientId = userData?.id;

			CoreHttpHandler.request('emailCampaign', 'sendEmail', payload,
				response => {
					setSendingEmail(false);
					if (onSend) onSend(response.data, 'Email sent successfully!');
					onClose();
				},
				error => {
					console.error('Error sending email:', error);
					showSnackbar('Failed to send email. Please try again.', 'error');
					setSendingEmail(false);
				}
			);
		} catch (error) {
			console.error('Error sending email:', error);
			showSnackbar('Failed to send email. Please try again.', 'error');
			setSendingEmail(false);
		}
	};

	const isFormValid = () => {
		const hasContent = mode === 'compose' ? editorBody.trim() : selectedTemplate;
		const paramsValid = mode === 'template' ? areAllParamsFilled() : true;
		return toEmails.length > 0 && selectedDomain && selectedSenderEmail && hasContent && paramsValid && !uploadingAttachment;
	};

	const modalContent = (
		<div className={classes.formContainer}>
			{/* Sender Domain and Sender Email Row */}
			<div className={classes.twoColumnRow}>
				<div className={classes.fieldRow}>
					<Typography className={classes.fieldLabel}>
						Sender Domain <span className={classes.requiredStar}>*</span>
					</Typography>
					<SearchableDropdown
						label=""
						options={formattedDomainOptions}
						value={selectedDomain}
						onChange={handleDomainChange}
						placeholder={loadingDomains ? "Loading domains..." : "Select sender domain..."}
						fullWidth
						disabled={loadingDomains}
					/>
				</div>

				<div className={classes.fieldRow}>
					<Typography className={classes.fieldLabel}>
						Sender Email <span className={classes.requiredStar}>*</span>
					</Typography>
					<SearchableDropdown
						label=""
						options={formattedSenderEmailOptions}
						value={selectedSenderEmail}
						onChange={handleSenderEmailChange}
						placeholder={
							!selectedDomain
								? "Select domain first..."
								: loadingSenderEmails
									? "Loading emails..."
									: senderEmails.length === 0
										? "No emails found"
										: "Select sender email..."
						}
						fullWidth
						disabled={!selectedDomain || loadingSenderEmails || senderEmails.length === 0}
					/>
					{loadingSenderEmails && (
						<Typography className={classes.loadingText}>Loading sender emails...</Typography>
					)}
				</div>
			</div>

			<div className={classes.fieldRow}>
				<Typography className={classes.fieldLabel}>To <span className={classes.requiredStar}>*</span></Typography>
				<div className={classes.emailPillsContainer} onClick={() => document.getElementById('to-email-input')?.focus()}>
					{toEmails.map((email, index) => (
						<div key={index} className={classes.emailPill}>
							<span className={classes.emailPillText}>{email}</span>
							<span className={classes.emailPillRemove} onClick={(e) => { e.stopPropagation(); handleRemoveEmail(email); }}><CloseIcon style={{ fontSize: 16 }} /></span>
						</div>
					))}
					<input id="to-email-input" type="email" className={classes.emailInput} value={toInput} onChange={handleToInputChange} onKeyDown={handleToKeyDown}
						placeholder={toEmails.length === 0 ? 'recipient@email.com (press Enter or Space)' : ''} />
				</div>
				{toError && <Typography className={classes.errorText}>{toError}</Typography>}
			</div>

			<div className={classes.fieldRow}>
				<Typography className={classes.fieldLabel}>Subject <span className={classes.requiredStar}>*</span></Typography>
				<FormFieldOmni variant="outlined" value={formData.subject} onChange={handleFieldChange('subject')} size="small" fullWidth placeholder="Email subject" inputProps={{ maxLength: 100 }} />
				<Typography className={`${classes.characterCount} ${subjectLength >= 100 ? classes.characterCountError : subjectLength >= 80 ? classes.characterCountWarning : ''}`}>
					{subjectLength}/100 characters
				</Typography>
			</div>

			<div className={classes.fieldRow}>
				<Typography className={classes.fieldLabel}>Email Content <span className={classes.requiredStar}>*</span></Typography>
				<div className={classes.modeToggle}>
					<button
						className={`${classes.modeButton} ${mode === 'compose' ? classes.modeButtonActive : ''}`}
						onClick={() => setMode('compose')}
						type="button"
					>
						Compose
					</button>
					<button className={`${classes.modeButton} ${mode === 'template' ? classes.modeButtonActive : ''}`} onClick={() => setMode('template')} type="button">Use Template</button>
				</div>
			</div>

			{mode === 'compose' && (
				<div className={classes.fieldRow}>
					{/* <Typography className={classes.fieldLabel} style={{ marginBottom: 8 }}>
						<span style={{ fontWeight: 400, fontSize: 13, color: '#6B7280' }}>(Use param button or type {'{{1}}'}, {'{{2}}'}, etc. for personalization)</span>
					</Typography> */}
					<div className={classes.editorSection}>
						<div className={classes.tabsContainer}>
							<Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" TabIndicatorProps={{ style: { display: 'none' } }}>
								<Tab className={classes.tab} label={<Box display="flex" alignItems="center"><EditIcon className={classes.tabIcon} />Compose</Box>} />
								<Tab className={classes.tab} label={<Box display="flex" alignItems="center"><VisibilityIcon className={classes.tabIcon} />Preview</Box>} />
								<Tab className={classes.tab} label={<Box display="flex" alignItems="center"><CodeIcon className={classes.tabIcon} />HTML</Box>} />
							</Tabs>
						</div>
						{activeTab === 0 && (
							<RichTextEditor value={editorBody} onChange={handleEditorBodyChange} minHeight={250} maxHeight={400}
								showCharacterCount={true} showParamButton={true} onImageUpload={handleImageUpload}
								toolbarOptions={[
									'undo', 'redo', '|',
									'heading', '|',
									'fontFamily', 'fontSize', 'fontSizeControls', '|',
									'bold', 'italic', 'underline', 'strikethrough', '|',
									'textColor', 'bgColor', '|',
									'bulletList', 'numberedList', '|',
									'alignLeft', 'alignCenter', 'alignRight', '|',
									'link', 'image', '|',
									'blockquote', 'code', '|',
									'clearFormat', '|',
									'param'
								]} />
						)}
						{activeTab === 1 && (
							<div className={classes.previewContainer}>
								{editorBody ? <div className={classes.previewContent} dangerouslySetInnerHTML={{ __html: editorBody }} />
									: <div className={classes.emptyPreview}>No content to preview. Start typing in the Compose tab.</div>}
							</div>
						)}
						{activeTab === 2 && (
							<div className={classes.htmlCodeContainer}>
								<div className={classes.htmlCodeHeader}>
									<button className={classes.pasteButton} onClick={pasteHtmlFromClipboard}><ContentPasteIcon style={{ fontSize: 16 }} />Paste HTML</button>
									<div className={classes.htmlButtonsRow}>
										{editorBody && <button className={classes.clearButton} onClick={clearHtmlContent}><ClearIcon style={{ fontSize: 16 }} />Clear</button>}
										<button className={`${classes.copyButton} ${htmlCopied ? classes.copyButtonSuccess : ''}`} onClick={copyHtmlToClipboard}>
											{htmlCopied ? <><CheckIcon style={{ fontSize: 16 }} />Copied!</> : <><ContentCopyIcon style={{ fontSize: 16 }} />Copy HTML</>}
										</button>
									</div>
								</div>
								{editorBody ? <pre className={classes.htmlCode}>{formatHtml(generateCompleteHTML(editorBody))}</pre>
									: <div className={classes.emptyPreview}>No HTML content. Start typing in the Compose tab or paste HTML.</div>}
							</div>
						)}
					</div>
				</div>
			)}

			{mode === 'template' && (
				<>
					<div className={classes.fieldRow}>
						<SearchableDropdown label="" options={formattedTemplateOptions} value={selectedTemplate} onChange={handleTemplateChange} placeholder="Search and select template..." fullWidth />
					</div>
					{detectedParams.length > 0 && (
						<div className={classes.paramsSection}>
							<Typography className={classes.paramsTitle}>Template Parameters ({detectedParams.length})</Typography>
							{detectedParams.map((param) => (
								<div key={param} className={classes.paramField}>
									<Typography className={classes.paramLabel}>Parameter {param} <span className={classes.requiredStar}>*</span></Typography>
									<FormFieldOmni variant="outlined" value={paramValues[param] || ''} onChange={handleParamChange(param)} size="small" fullWidth placeholder={`Enter value for ${param}`} />
								</div>
							))}
						</div>
					)}
					{selectedTemplate && (
						<>
							<Typography className={classes.previewLabel}>Preview</Typography>
							{detectedParams.length > 0 && (
								<div className={classes.previewLegend}>
									<div className={classes.legendItem}><span className={classes.legendFilledBadge}>Value</span><span className={classes.legendText}>= Filled parameter</span></div>
									<div className={classes.legendItem}><span className={classes.legendUnfilledBadge}>{'{{1}}'}</span><span className={classes.legendText}>= Unfilled parameter</span></div>
								</div>
							)}
							<div className={classes.previewContainer}><div className={classes.previewContent} dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} /></div>
						</>
					)}
				</>
			)}

			{attachments.length > 0 && (
				<div className={classes.attachmentsContainer}>
					{attachments.map(attachment => (
						<div key={attachment.id} className={classes.attachmentItem}>
							<InsertDriveFileOutlinedIcon className={classes.attachmentIcon} />
							<div className={classes.attachmentInfo}>
								<Typography className={classes.attachmentName}>{attachment.name}</Typography>
								{attachment.size && <Typography className={classes.attachmentSize}>{attachment.size}</Typography>}
							</div>
							<IconButton size="small" className={classes.attachmentRemove} onClick={() => handleRemoveAttachment(attachment.id)}><CloseIcon fontSize="small" /></IconButton>
						</div>
					))}
				</div>
			)}

			{/* Save as Template Dialog */}
			<Dialog
				open={saveTemplateDialogOpen}
				onClose={handleCloseSaveTemplateDialog}
				className={classes.saveTemplateDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle className={classes.saveTemplateDialogTitle}>
					Save as Template
				</DialogTitle>
				<DialogContent className={classes.saveTemplateDialogContent}>
					<div className={classes.fieldRow}>
						<Typography className={classes.fieldLabel}>
							Template Name <span className={classes.requiredStar}>*</span>
						</Typography>
						<FormFieldOmni
							variant="outlined"
							value={templateName}
							onChange={handleTemplateNameChange}
							size="small"
							fullWidth
							placeholder="Enter template name (spaces will be replaced with _)"
							inputProps={{ maxLength: 100 }}
							error={!!templateNameError}
							className={templateNameError ? classes.inputError : ''}
							autoFocus
						/>
						{templateNameError && (
							<Typography className={classes.templateNameError}>
								{templateNameError}
							</Typography>
						)}
						{!templateNameError && (
							<Typography
								className={`${classes.characterCount} ${templateNameLength >= 100
									? classes.characterCountError
									: templateNameLength >= 80
										? classes.characterCountWarning
										: ''
									}`}
							>
								{templateNameLength}/100 characters
							</Typography>
						)}
					</div>
				</DialogContent>
				<DialogActions className={classes.saveTemplateDialogActions}>
					<ButtonOmni
						onClick={handleCloseSaveTemplateDialog}
						variant="outlined"
						label="Cancel"
						size="small"
						disabled={savingTemplate}
					/>
					<ButtonOmni
						onClick={handleSaveAsTemplate}
						label={savingTemplate ? 'Saving...' : 'Save Template'}
						size="small"
						loading={savingTemplate}
						disabled={savingTemplate || !templateName.trim() || !!templateNameError}
					/>
				</DialogActions>
			</Dialog>

			<Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
				<Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
			</Snackbar>
		</div>
	);

	const modalActions = (
		<div className={classes.bottomActions}>
			<div className={classes.leftActions}>
				<ButtonOmni
					onClick={handleAttachFile}
					size="small"
					label={"Attachments"}
					startIcon={<AttachFileIcon />}
					disabled={uploadingAttachment}
				/>
			</div>
			<div className={classes.rightActions}>
				{mode === 'compose' && editorBody.trim() && (
					<ButtonOmni
						onClick={handleOpenSaveTemplateDialog}
						size="small"
						label="Save as Template"
						variant="outlined"
						startIcon={<SaveIcon />}
						disabled={templateSaved}
					/>
				)}
				<ButtonOmni onClick={handleSend} size="small" label={sendingEmail ? 'Sending...' : 'Send'} loading={sendingEmail} disabled={!isFormValid() || sendingEmail} />
			</div>
		</div>
	);

	return <ModalOmni open={open} handleClose={onClose} title="New Message" content={modalContent} actions={modalActions} customWidth={700} disableBackdropClick={true} disableEscapeKeyDown={true} />;
}

export default ComposePushEmailModal;